import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import PhotoUploader from "../components/PhotoUploader";
import ConditionChecklist from "../components/ConditionChecklist";
import QualityAssuranceDashboard from "../components/QualityAssuranceDashboard";
import { isQualityAssuranceUiEnabled } from "../config/featureFlags";
import { listingsService } from "../services/listingsService";
import { supabase } from "../lib/supabase";

export default function CreateListing() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();

  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("Pre-natal");
  const [ageRange, setAgeRange] = useState("0-3M");
  const [customAgeRange, setCustomAgeRange] = useState("");
  const [condition, setCondition] = useState("good");
  const [tags, setTags] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [geoStatus, setGeoStatus] = useState("");
  const [showQADashboard, setShowQADashboard] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!user) {
      setError("You must be logged in to create a listing");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let lat = latitude;
      let lon = longitude;
      // If lat/lon not set, try to geocode from location text
      if ((!lat || !lon) && location.trim() !== "") {
        try {
          const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(location)}`;
          const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
          const data = await res.json();
          if (data && data.length > 0) {
            lat = data[0].lat;
            lon = data[0].lon;
            setLatitude(lat);
            setLongitude(lon);
            setGeoStatus('Location geocoded!');
          } else {
            setGeoStatus('Could not geocode location');
            setError('Could not determine the location. Please enter a more specific city/area.');
            setLoading(false);
            return;
          }
        } catch (err) {
          setGeoStatus('Could not geocode location');
          setError('Could not determine the location. Please enter a more specific city/area.');
          setLoading(false);
          return;
        }
      }
      // Block submission if lat/lon are still not set
      if (!lat || !lon) {
        setError('Could not determine the location. Please enter a more specific city/area.');
        setLoading(false);
        return;
      }
      // First create the listing without photos
      const listingData = {
        title,
        price_cents: Math.round(Number(price || 0) * 100),
        category,
        age_range: ageRange === "custom" ? customAgeRange : ageRange,
        condition,
        description,
        location,
        latitude: lat ? Number(lat) : null,
        longitude: lon ? Number(lon) : null,
        user_id: user.id,
        status: 'active',
        tags: tags
      };

      const createdListing = await listingsService.createListing(listingData);

      // Upload photos if any
      let photoUrls = [];
      if (photos.length > 0) {
        for (let i = 0; i < photos.length; i++) {
          const photo = photos[i];
          if (photo instanceof File) {
            const url = await listingsService.uploadImage(photo, createdListing.id);
            photoUrls.push(url);
          } else {
            // If it's already a URL (from preview), just add it
            photoUrls.push(photo);
          }
        }

        // Update the listing with photo URLs
        const { error: updateError } = await supabase
          .from('listings')
          .update({ photos: photoUrls })
          .eq('id', createdListing.id);

        if (updateError) throw updateError;
      }

      nav(`/listing/${createdListing.id}`);
    } catch (err) {
      console.error('Error creating listing:', err);
      setError(err.message || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-center mx-auto max-w-2xl">
      {/* Page Header */}
      <div className="mb-8 text-center">
        <h1 className="text-responsive-xl font-bold text-slate-900 mb-2">
          {t('page.createListing')}
        </h1>
      </div>

      {/* Form Container */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-white/40 shadow-xl p-8">
        <form onSubmit={submit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="form-label">{t('listing.title')}</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-input"
              placeholder={t('listing.titlePlaceholder', 'e.g., Bundle of newborn onesies')}
              required
            />
          </div>
          {/* Location */}
          <div className="space-y-2">
            <label className="form-label">{t('listing.location')}</label>
            <div className="location-input-container flex gap-2 items-center flex-nowrap">
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                className="form-input location-input flex-1 min-w-0"
                placeholder="e.g., Toronto, ON or Downtown Vancouver"
                required
              />
              <button
                type="button"
                className="location-button flex items-center justify-center bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-colors duration-200 w-12 h-12 sm:w-10 sm:h-10 flex-shrink-0"
                onClick={async () => {
                  setGeoStatus('Getting location...');
                  if (!navigator.geolocation) {
                    setGeoStatus('Geolocation not supported');
                    return;
                  }
                  
                  try {
                    const pos = await new Promise((resolve, reject) => {
                      navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 60000
                      });
                    });

                    setLatitude(pos.coords.latitude);
                    setLongitude(pos.coords.longitude);
                    setGeoStatus('Location set!');
                    
                    // Nominatim reverse geocoding
                    try {
                      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`;
                      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
                      const data = await res.json();
                      if (data && (data.address?.city || data.address?.town || data.address?.village || data.display_name)) {
                        const city = data.address.city || data.address.town || data.address.village || '';
                        const state = data.address.state || '';
                        const country = data.address.country || '';
                        let loc = city;
                        if (state) loc += (loc ? ', ' : '') + state;
                        if (country) loc += (loc ? ', ' : '') + country;
                        setLocation(loc || data.display_name || '');
                        setGeoStatus('Location auto-filled!');
                      } else {
                        setGeoStatus('Could not determine address');
                      }
                    } catch (err) {
                      setGeoStatus('Could not determine address');
                    }
                  } catch (err) {
                    console.error('Geolocation error:', err);
                    if (err.code === 1) {
                      setGeoStatus('Location access denied - Please allow location access in your browser settings or enter location manually');
                    } else if (err.code === 2) {
                      setGeoStatus('Location unavailable - Please enter your location manually');
                    } else if (err.code === 3) {
                      setGeoStatus('Location request timeout - Please try again or enter location manually');
                    } else {
                      setGeoStatus('Could not get location - Please enter your location manually');
                    }
                  }
                }}
                title="Use my current location"
              >
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
            {geoStatus && (
              <div className={`text-xs mt-1 ${
                geoStatus.includes('error') || geoStatus.includes('denied') || geoStatus.includes('unavailable') || geoStatus.includes('timeout') || geoStatus.includes('Could not')
                  ? 'text-red-500' 
                  : 'text-green-600'
              }`}>
                {geoStatus}
              </div>
            )}
            {geoStatus && geoStatus.includes('denied') && (
              <div className="text-xs text-slate-500 mt-1 p-2 bg-slate-50 rounded-lg">
                💡 <strong>Tip:</strong> To use location automatically, allow location access when prompted by your browser. You can also manually type your city or area above.
              </div>
            )}
          </div>

          {/* Price and Category Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="form-label">{t('listing.price')} (CAD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-semibold">$</span>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="form-input pl-10"
                  inputMode="decimal"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="form-label">{t('listing.category')}</label>
              <select 
                value={category} 
                onChange={(e) => setCategory(e.target.value)} 
                className="form-select"
              >
                <option value="Pre-natal">🤰 Pre-natal</option>
                <option value="Clothing">👕 Clothing</option>
                <option value="Gear">🚗 Gear</option>
                <option value="Toys">🧸 Toys</option>
                <option value="Feeding">🍼 Feeding</option>
                <option value="Nursery">🛏️ Nursery</option>
                <option value="Other">📦 Other</option>
              </select>
            </div>
          </div>

          {/* Age Range */}
          <div className="space-y-2">
            <label className="form-label">Age Range</label>
            <select 
              value={ageRange} 
              onChange={(e) => setAgeRange(e.target.value)} 
              className="form-select"
            >
              <option value="0-3M">👶 0-3 months (Newborn)</option>
              <option value="3-6M">🍼 3-6 months</option>
              <option value="6-12M">🧸 6-12 months</option>
              <option value="12-24M">🚶 12-24 months (Toddler)</option>
              <option value="2-4Y">🏃 2-4 years (Preschool)</option>
              <option value="4Y+">🎒 4+ years (School age)</option>
              <option value="All Ages">🌟 All Ages</option>
              <option value="custom">✏️ Custom Range</option>
            </select>
            
            {ageRange === "custom" && (
              <div className="mt-2">
                <input
                  type="text"
                  value={customAgeRange}
                  onChange={(e) => setCustomAgeRange(e.target.value)}
                  className="form-input"
                  placeholder="e.g., 4-6M, 18-24M, 3-5Y"
                  required={ageRange === "custom"}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter a custom age range (e.g., 4-6M for 4-6 months)
                </p>
              </div>
            )}
            
            <p className="text-xs text-slate-500">
              Select the age range this item is suitable for
            </p>
          </div>

          {/* Condition */}
          <div className="space-y-3">
            <label className="form-label">Condition</label>
            <ConditionChecklist value={condition} onChange={setCondition} />
          </div>

          {/* Tags */}
          <div className="space-y-3">
            <label className="form-label">Tags (Optional)</label>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'moving-out', label: 'Moving Out', emoji: '📦' },
                  { id: 'give-away', label: 'Give Away', emoji: '🎁' },
                  { id: 'urgent', label: 'Urgent Sale', emoji: '⚡' },
                  { id: 'bundle', label: 'Bundle Deal', emoji: '📦' },
                  { id: 'new', label: 'Brand New', emoji: '✨' },
                  { id: 'negotiable', label: 'Price Negotiable', emoji: '💰' },
                  { id: 'pickup-only', label: 'Pickup Only', emoji: '🚚' }
                ].map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => {
                      if (tags.includes(tag.id)) {
                        setTags(tags.filter(t => t !== tag.id));
                      } else {
                        setTags([...tags, tag.id]);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      tags.includes(tag.id)
                        ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                        : 'bg-slate-100 text-slate-700 border-2 border-transparent hover:bg-slate-200'
                    }`}
                  >
                    <span>{tag.emoji}</span>
                    <span>{tag.label}</span>
                  </button>
                ))}
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tagId) => {
                    const tag = [
                      { id: 'moving-out', label: 'Moving Out', emoji: '📦' },
                      { id: 'give-away', label: 'Give Away', emoji: '🎁' },
                      { id: 'urgent', label: 'Urgent Sale', emoji: '⚡' },
                      { id: 'bundle', label: 'Bundle Deal', emoji: '📦' },
                      { id: 'new', label: 'Brand New', emoji: '✨' },
                      { id: 'negotiable', label: 'Price Negotiable', emoji: '💰' },
                      { id: 'pickup-only', label: 'Pickup Only', emoji: '🚚' }
                    ].find(t => t.id === tagId);
                    return (
                      <span key={tagId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        <span>{tag?.emoji}</span>
                        <span>{tag?.label}</span>
                        <button
                          type="button"
                          onClick={() => setTags(tags.filter(t => t !== tagId))}
                          className="ml-1 hover:text-blue-900"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Add tags to help buyers understand your listing better (e.g., moving out, give away, urgent sale)
            </p>
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <label className="form-label">Photos</label>
            <PhotoUploader value={photos} onChange={setPhotos} />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="form-label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-input resize-none"
              rows={4}
              placeholder="Any details buyers should know (manufacture date for car seats, stains, etc.)"
            />
            <p className="text-xs text-slate-500">
              Include details like size, brand, condition notes, and any special features.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs">!</span>
                </div>
                <div className="text-sm text-red-800">{error}</div>
              </div>
            </div>
          )}

          {isQualityAssuranceUiEnabled && (
            <div className="pt-6 border-t border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Quality Assurance</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Complete quality checks to build buyer trust and ensure safety
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowQADashboard(!showQADashboard)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {showQADashboard ? 'Hide QA' : 'Start QA'}
                </button>
              </div>

              {showQADashboard && (
                <div className="mb-6">
                  <QualityAssuranceDashboard 
                    listingId={null} // Will be set after listing is created
                    listingCategory={category}
                    onComplete={() => {
                      // Refresh QA status if needed
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <button 
              type="submit" 
              className="w-full btn-primary text-lg py-4 disabled:opacity-60"
              disabled={loading}
            >
              <span className="flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('listing.creating')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    {t('listing.publish')}
                  </>
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}